import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import UserHeader from '../components/UserHeader';
import { getParasha } from '../utils/userSelections';
import tora from '../../data/torah.json';
import ashkenazAlyiot from '../../data/ashkenaz_alyiot.json';

const BLUE_ACCENT = '#3B7BF4';
const GOLD = '#F5C842';
const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#D1D5DB';

const ALIYAH_INDEX = 5; // 6th aliyah (0-indexed)

type TorahVerse = {
  id: number;
  book: string;
  chapter: number;
  pasuk: number;
  hebrew: string;
  transliteration?: string;
  parasha?: string;
};

type AliyahRange = {
  start: { chapter: number; pasuk: number };
  end: { chapter: number; pasuk: number };
};

function parseAliyahRange(range: string): AliyahRange | null {
  const parts = range.split('-');
  if (parts.length < 2) return null;
  const parsePart = (s: string) => {
    const [c, p] = s.split(':').map(Number);
    return { chapter: c, pasuk: p };
  };
  return { start: parsePart(parts[0]), end: parsePart(parts[1]) };
}

function isVerseInRange(v: TorahVerse, r: AliyahRange): boolean {
  const cmp = (a: { chapter: number; pasuk: number }, b: { chapter: number; pasuk: number }) =>
    a.chapter !== b.chapter ? a.chapter - b.chapter : a.pasuk - b.pasuk;
  return cmp(v, r.start) >= 0 && cmp(v, r.end) <= 0;
}

const WaveformIcon: React.FC = () => (
  <svg width="40" height="18" viewBox="0 0 40 18" fill="none">
    {[2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35, 38].map((x, i) => {
      const h = [4, 10, 14, 8, 16, 12, 18, 10, 14, 8, 12, 6, 4][i];
      return (
        <rect
          key={x}
          x={x}
          y={(18 - h) / 2}
          width="2"
          height={h}
          rx="1"
          fill="rgba(255,255,255,0.7)"
        />
      );
    })}
  </svg>
);

const YamenPracticePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isHebrew = i18n.language?.startsWith('he');

  const storedParasha = getParasha();
  const parashaKey = storedParasha?.english ?? 'Bereshit';
  const parashaHebrew = storedParasha?.hebrew ?? 'בראשית';

  const aliyahVerses: TorahVerse[] = useMemo(() => {
    const parashaAliyot = (ashkenazAlyiot as Record<string, string[]>)[parashaKey];
    if (!parashaAliyot || parashaAliyot.length <= ALIYAH_INDEX) return [];
    const range = parseAliyahRange(parashaAliyot[ALIYAH_INDEX]);
    if (!range) return [];
    return (tora as TorahVerse[]).filter(
      (v) => v.parasha === parashaKey && isVerseInRange(v, range),
    );
  }, [parashaKey]);

  const [verseIdx, setVerseIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(8);
  const [isSaved, setIsSaved] = useState(false);
  const [score] = useState(85);

  const verse = aliyahVerses[verseIdx];

  // Split verse into words – first 3 get gold highlight (simulating focus words)
  const renderVerseText = (hebrew: string) => {
    const words = hebrew.replace(/׃/g, '').trim().split(/\s+/);
    return words.map((word, i) => (
      <span
        key={i}
        style={{
          color: i < 3 ? GOLD : 'white',
          marginInlineEnd: 6,
        }}
      >
        {word}
      </span>
    ));
  };

  const nextVerse = () => setVerseIdx((i) => Math.min(aliyahVerses.length - 1, i + 1));

  const aliyahLabel = t('yamen_aliyah_sixth', 'עליה שישית - נוסח תימן');
  const chapterLabel = verse ? `${t('chapter_short', 'פרק')} ${verse.chapter}` : '';
  const pasukLabel = verse
    ? `${t('verse_short', 'פסוק')} ${String(verse.pasuk)}${isHebrew ? "'" : ''}`
    : '';
  const partLabel = `${t('part_short', 'חלק')} 2 ${t('of', 'מתוך')} 3`;

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        background: '#f0f4fb',
        fontFamily: "var(--kol-font-sans, 'Inter', system-ui, sans-serif)",
        color: TEXT_DARK,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <UserHeader />
      <div
        style={{
          padding: '18px 18px 110px',
          boxSizing: 'border-box',
          direction: 'rtl',
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <span style={{ color: GOLD, fontSize: 20 }}>✦</span>
            <span style={{ fontSize: 42, fontWeight: 900, color: TEXT_DARK, lineHeight: 1 }}>
              {parashaHebrew}
            </span>
            <span style={{ color: GOLD, fontSize: 20 }}>✦</span>
          </div>
          <div style={{ fontSize: 15, color: TEXT_SECONDARY, marginTop: 6 }}>{aliyahLabel}</div>
        </div>

        {/* Navigation pills */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0,
            margin: '16px 0',
            background: 'white',
            borderRadius: 12,
            border: `1px solid ${BORDER_LIGHT}`,
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          {[
            { icon: '📖', label: chapterLabel },
            { icon: '🗂', label: partLabel },
            { icon: '🔖', label: pasukLabel },
          ].map((item, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <div style={{ width: 1, height: 28, background: BORDER_LIGHT, flexShrink: 0 }} />
              )}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '12px 8px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: TEXT_DARK,
                }}
              >
                <span style={{ fontSize: 14 }}>{item.icon}</span>
                {item.label}
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Blue practice card */}
        <div
          style={{
            background: BLUE_ACCENT,
            borderRadius: 24,
            padding: '20px 20px 16px',
            boxShadow: '0 20px 40px rgba(59,123,244,0.35)',
          }}
        >
          {/* Waveform icon */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <WaveformIcon />
          </div>

          {/* Verse text */}
          <div
            style={{
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1.7,
              textAlign: 'center',
              marginBottom: 20,
              minHeight: 90,
              direction: 'rtl',
            }}
          >
            {verse ? (
              renderVerseText(verse.hebrew)
            ) : (
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16 }}>
                {t('no_verses_found', 'לא נמצאו פסוקים')}
              </span>
            )}
          </div>

          {/* Audio player */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 16,
              direction: 'ltr',
            }}
          >
            <button
              onClick={() => setIsPlaying((p) => !p)}
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'white',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                cursor: 'pointer',
                flexShrink: 0,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
            <span style={{ color: 'white', fontSize: 12, flexShrink: 0 }}>
              0:{String(audioProgress).padStart(2, '0')}
            </span>
            <input
              type="range"
              min={0}
              max={24}
              value={audioProgress}
              onChange={(e) => setAudioProgress(Number(e.target.value))}
              style={{ flex: 1, accentColor: 'white', cursor: 'pointer' }}
            />
            <span style={{ color: 'white', fontSize: 12, flexShrink: 0 }}>0:24</span>
            <button
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                cursor: 'pointer',
                flexShrink: 0,
                color: 'white',
              }}
            >
              🔊
            </button>
          </div>

          {/* Record button */}
          <button
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: 999,
              background: GOLD,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 14,
              cursor: 'pointer',
              marginBottom: 12,
              boxShadow: '0 8px 20px rgba(245,200,66,0.45)',
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                flexShrink: 0,
              }}
            >
              🎤
            </div>
            <span style={{ fontSize: 22, fontWeight: 800, color: TEXT_DARK }}>
              {t('record_yourself', 'הקליט את עצמך')}
            </span>
          </button>

          {/* Action buttons row */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <button
              style={{
                flex: 1,
                padding: '12px 8px',
                borderRadius: 999,
                background: 'transparent',
                border: '1.5px solid rgba(255,255,255,0.5)',
                color: 'white',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 16 }}>🔄</span>
              {t('listen_again', 'הקשב שוב')}
            </button>
            <button
              style={{
                flex: 1,
                padding: '12px 8px',
                borderRadius: 999,
                background: 'transparent',
                border: '1.5px solid rgba(255,255,255,0.5)',
                color: 'white',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 16 }}>👥</span>
              {t('compare', 'השוואה')}
            </button>
          </div>

          {/* Status pills row */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginBottom: 14,
              direction: 'rtl',
            }}
          >
            <button
              onClick={() => setIsSaved((s) => !s)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 999,
                background: isSaved ? '#22c55e' : 'rgba(255,255,255,0.15)',
                border: isSaved ? 'none' : '1.5px solid rgba(255,255,255,0.4)',
                color: 'white',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {isSaved && <span style={{ fontSize: 14 }}>✓</span>}
              {t('save', 'שמור')}
            </button>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.15)',
                border: '1.5px solid rgba(255,255,255,0.4)',
                color: GOLD,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <span>⭐</span>
              {t('score', 'ציון')} {score}%
            </div>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.15)',
                border: '1.5px solid rgba(255,255,255,0.4)',
                color: 'white',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              <span>📊</span>
              {t('compare', 'השוואה')}
            </button>
          </div>

          {/* Verse preview / navigation */}
          <div
            style={{
              background: 'white',
              borderRadius: 14,
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              direction: 'rtl',
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  color: TEXT_DARK,
                  lineHeight: 1.6,
                  direction: 'rtl',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {verse?.hebrew.replace(/׃/g, '').trim() ?? ''}
              </div>
              {/* Verse dots indicator */}
              <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                {aliyahVerses.slice(0, Math.min(aliyahVerses.length, 8)).map((_, i) => (
                  <div
                    key={i}
                    onClick={() => setVerseIdx(i)}
                    style={{
                      width: i === verseIdx ? 18 : 6,
                      height: 6,
                      borderRadius: 999,
                      background: i === verseIdx ? BLUE_ACCENT : '#d1d5db',
                      cursor: 'pointer',
                      transition: 'width 0.2s',
                    }}
                  />
                ))}
              </div>
            </div>
            <button
              onClick={nextVerse}
              disabled={verseIdx >= aliyahVerses.length - 1}
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: BLUE_ACCENT,
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: 18,
                cursor: verseIdx < aliyahVerses.length - 1 ? 'pointer' : 'not-allowed',
                opacity: verseIdx < aliyahVerses.length - 1 ? 1 : 0.4,
                flexShrink: 0,
              }}
            >
              ›
            </button>
          </div>
        </div>

        {/* Back navigation */}
        <button
          onClick={() => navigate(-1)}
          style={{
            marginTop: 16,
            width: '100%',
            padding: '14px',
            borderRadius: 14,
            background: 'white',
            border: `1px solid ${BORDER_LIGHT}`,
            color: TEXT_SECONDARY,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          {isHebrew ? '→' : '←'} {t('back', 'חזור')}
        </button>
      </div>

      <BottomNav />
    </div>
  );
};

export default YamenPracticePage;
